"use client";

// Importamos varios tipos y funciones de la biblioteca `@tanstack/react-table`
// que nos ayudarán a configurar la tabla.
import {
	type ColumnFiltersState,
	type PaginationState,
	type SortingState,
	type TableOptions,
	type TableState,
	type VisibilityState,
	getCoreRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";

// Importamos funciones de navegación y manejo de URL desde Next.js
import { usePathname, useRouter, useSearchParams } from "next/navigation";

// Importamos React y Zod para manejar la validación de datos.
import * as React from "react";
import { z } from "zod";

// Importamos tipos personalizados y otros hooks internos.
import type { DataTableFilterField } from "@/components/ui/data-table/types";
import { useDebounce } from "@/features/core/hooks/use-debounce";
import { useQueryString } from "@/features/core/hooks/use-query-string";

// Definimos la interfaz de las propiedades que recibirá el hook `useDataTable`.
interface UseDataTableProps<TData>
	extends Omit<
		TableOptions<TData>,
		| "pageCount"
		| "getCoreRowModel"
		| "manualFiltering"
		| "manualPagination"
		| "manualSorting"
	>,
	Required<Pick<TableOptions<TData>, "pageCount">> {
	/**
	 * Defines filter fields for the table. Supports both dynamic faceted filters and search filters.
	 * - Faceted filters are rendered when `options` are provided for a filter field.
	 * - Otherwise, search filters are rendered.
	 *
	 * The indie filter field `value` represents the corresponding column name in the database table.
	 * @default []
	 * @type { label: string, value: keyof TData, placeholder?: string, options?: { label: string, value: string, icon?: React.ComponentType<{ className?: string }> }[] }[]
	 * @example
	 * ```ts
	 * // Render a search filter
	 * const filterFields = [
	 *   { label: "Title", value: "title", placeholder: "Search titles" }
	 * ];
	 * // Render a faceted filter
	 * const filterFields = [
	 *   {
	 *     label: "Status",
	 *     value: "status",
	 *     options: [
	 *       { label: "Todo", value: "todo" },
	 *       { label: "In Progress", value: "in-progress" },
	 *     ]
	 *   }
	 * ];
	 * ```
	 */
	filterFields?: DataTableFilterField<TData>[];

	/**
	 * Enable notion like column filters.
	 * Advanced filters and column filters cannot be used at the same time.
	 * @default false
	 * @type boolean
	 */
	enableAdvancedFilter?: boolean;

	/**
	 * The method to use when updating the URL.
	 * - "push" - Pushes a new entry onto the history stack.
	 * - "replace" - Replaces the current entry on the history stack.
	 * @default "replace"
	 */
	method?: "push" | "replace";

	/**
	 * Indicates whether the page should scroll to the top when the URL changes.
	 * @default false
	 */
	scroll?: boolean;

	/**
	 * A callback function that is called before updating the URL.
	 * Can be use to retrieve the loading state of the route transition.
	 * @see https://react.dev/reference/react/useTransition
	 *
	 */
	startTransition?: React.TransitionStartFunction;

	// Extend to make the sorting id typesafe
	initialState?: Omit<Partial<TableState>, "sorting"> & {
		sorting?: {
			id: Extract<keyof TData, string>;
			desc: boolean;
		}[];
	};
}

// Definimos un esquema de validación para los parámetros de búsqueda en la URL usando Zod.
const searchParamsSchema = z.object({
	page: z.coerce.number().default(1),
	per_page: z.coerce.number().optional(),
	sort: z.string().optional(),
});

// Definimos el hook `useDataTable`, que maneja el estado de la tabla y la sincronización con la URL.
export function useDataTable<TData>({
	pageCount = -1,
	filterFields = [],
	enableAdvancedFilter = false,
	method = "replace", // Método de actualización de la URL.
	scroll = false,
	startTransition,
	...props
}: UseDataTableProps<TData>) {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	// Obtenemos los parámetros de la URL con `searchParamsSchema`.
	const search = searchParamsSchema.parse(Object.fromEntries(searchParams));

	const page = search.page ?? 1;
	const perPage = search.per_page ?? 10;

	// Definimos el orden de la tabla con el parámetro `sort`, usando el estado inicial si no hay ninguno en la URL.
	const sort =
		search.sort ??
		`${props.initialState?.sorting?.[0]?.id}.${props.initialState?.sorting?.[0]?.desc ? "desc" : "asc"
		}`;
	const [column, order] = sort?.split(".") ?? [];

	// Computamos columnas "buscables" y "filtrables" a partir de `filterFields`, usando memoización para optimizar.
	const { searchableColumns, filterableColumns } = React.useMemo(() => {
		return {
			searchableColumns: filterFields.filter((field) => !field.options),
			filterableColumns: filterFields.filter((field) => field.options),
		};
	}, [filterFields]);

	// Creamos una cadena de consulta para los filtros usando `useQueryString`.
	const { createQueryString } = useQueryString(searchParams);

	// Configuramos los filtros de columna iniciales en función de los parámetros de la URL.
	const initialColumnFilters: ColumnFiltersState = React.useMemo(() => {
		return Array.from(searchParams.entries()).reduce<ColumnFiltersState>(
			(filters, [key, value]) => {
				const filterableColumn = filterableColumns.find(
					(column) => column.value === key,
				);
				const searchableColumn = searchableColumns.find(
					(column) => column.value === key,
				);

				// Agrega el filtro correspondiente si existe en `filterableColumns` o `searchableColumns`.
				if (filterableColumn) {
					filters.push({
						id: key,
						value: value.split("."),
					});
				} else if (searchableColumn) {
					filters.push({
						id: key,
						value: [value],
					});
				}

				return filters;
			},
			[],
		);
	}, [filterableColumns, searchableColumns, searchParams]);

	// Definimos estados para la visibilidad de columnas y los filtros actuales.
	const [rowSelection, setRowSelection] = React.useState({});
	const [columnVisibility, setColumnVisibility] =
		React.useState<VisibilityState>({});
	const [columnFilters, setColumnFilters] =
		React.useState<ColumnFiltersState>(initialColumnFilters);

	// Estado y funciones para manejar la paginación de la tabla.
	const [{ pageIndex, pageSize }, setPagination] =
		React.useState<PaginationState>({
			pageIndex: page - 1,
			pageSize: perPage,
		});

	// Sincroniza el estado de paginación con la URL
	React.useEffect(() => {
		setPagination({
			pageIndex: (search.page ?? 1) - 1,
			pageSize: search.per_page ?? 10,
		});
	}, [search.page, search.per_page]);

	const pagination = React.useMemo(
		() => ({
			pageIndex,
			pageSize,
		}),
		[pageIndex, pageSize],
	);

	// Configuración inicial del estado de ordenación.
	const [sorting, setSorting] = React.useState<SortingState>([
		{
			id: column ?? "",
			desc: order === "desc",
		},
	]);

	// Efecto que actualiza la URL cuando cambian la paginación o el orden de la tabla.
	React.useEffect(() => {
		function onUrlChange() {
			const url = `${pathname}?${createQueryString({
				page: pageIndex + 1,
				per_page: pageSize,
				sort: sorting[0]?.id
					? `${sorting[0]?.id}.${sorting[0]?.desc ? "desc" : "asc"}`
					: null,
			})}`;

			// Actualiza la URL usando el método configurado.
			method === "push"
				? router.push(url, { scroll })
				: router.replace(url, { scroll });
		}

		// Utiliza `startTransition` si está disponible para mejorar el rendimiento.
		startTransition
			? startTransition(() => {
				onUrlChange();
			})
			: onUrlChange();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageIndex, pageSize, sorting, method, scroll]);

	// Filtrado con debounce para reducir las llamadas de actualización al cambiar filtros de búsqueda.
	const debouncedSearchableColumnFilters = JSON.parse(
		useDebounce(
			JSON.stringify(
				columnFilters.filter((filter) => {
					return searchableColumns.find((column) => column.value === filter.id);
				}),
			),
			500,
		),
	) as ColumnFiltersState;

	const filterableColumnFilters = columnFilters.filter((filter) => {
		return filterableColumns.find((column) => column.value === filter.id);
	});

	const [mounted, setMounted] = React.useState(false);

	// Efecto que maneja cambios de URL al aplicar filtros, excluyendo los filtros avanzados.
	React.useEffect(() => {
		// Opt out when advanced filter is enabled, because it contains additional params
		if (enableAdvancedFilter) return;

		// Prevent resetting the page on initial render
		if (!mounted) {
			setMounted(true);
			return;
		}

		// Initialize new params
		const newParamsObject = {
			page: 1,
		};

		// Actualiza los parámetros de búsqueda con filtros actuales.
		for (const column of debouncedSearchableColumnFilters) {
			if (typeof column.value === "string") {
				Object.assign(newParamsObject, {
					[column.id]: typeof column.value === "string" ? column.value : null,
				});
			}
		}

		for (const column of filterableColumnFilters) {
			if (typeof column.value === "object" && Array.isArray(column.value)) {
				Object.assign(newParamsObject, { [column.id]: column.value.join(".") });
			}
		}

		// Elimina los valores que ya no están en los filtros.
		for (const key of searchParams.keys()) {
			if (
				(searchableColumns.find((column) => column.value === key) &&
					!debouncedSearchableColumnFilters.find(
						(column) => column.id === key,
					)) ??
				(filterableColumns.find((column) => column.value === key) &&
					!filterableColumnFilters.find((column) => column.id === key))
			) {
				Object.assign(newParamsObject, { [key]: null });
			}
		}

		function onUrlChange() {
			const url = `${pathname}?${createQueryString(newParamsObject)}`;

			method === "push"
				? router.push(url, { scroll })
				: router.replace(url, { scroll });
		}

		startTransition
			? startTransition(() => {
				onUrlChange();
			})
			: onUrlChange();

		table.setPageIndex(0);
	}, [
		JSON.stringify(debouncedSearchableColumnFilters),
		JSON.stringify(filterableColumnFilters),
		method,
		scroll,
	]);

	// Creación de la tabla con la configuración y estado definidos.
	const table = useReactTable({
		...props,
		pageCount,
		state: {
			pagination,
			sorting,
			columnVisibility,
			rowSelection,
			columnFilters,
		},
		enableRowSelection: true,
		onRowSelectionChange: setRowSelection,
		onPaginationChange: setPagination,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		onColumnVisibilityChange: setColumnVisibility,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFacetedRowModel: getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		manualPagination: true,
		manualSorting: true,
		manualFiltering: true,
		enableColumnResizing: true,
		defaultColumn: {
			minSize: 0,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		}
	});

	return { table };
}
